import CloseIcon from "@mui/icons-material/Close";
import { Backdrop, CircularProgress, Dialog, IconButton } from "@mui/material";
import { useSnackbar } from "notistack";
import { Dispatch, SetStateAction, useEffect, useState, VFC } from "react";
import { useTranslation } from "react-i18next";
import { NavigateFunction, useNavigate } from "react-router";
import {
  OMOTI_COLLECTOR_SCRIPT,
  OMOTI_OV_SCRIPT,
} from "src/constants/omotiScripts";
import { useBodyScript } from "src/hooks/useBodyScript";
import { usePost } from "src/hooks/usePost";
import { appendBodyScript } from "src/utils/appendBodyScript";
import { setHtmlLang } from "src/utils/setHtmlLang";
import Button from "../components/common/Button";
import InputField from "../components/common/InputField";

interface Props {
  signIn?: (
    email: string,
    password: string,
    navigate: NavigateFunction,
    onSuccess: () => void,
    onError: () => void,
    path: string
  ) => Promise<boolean>;
  signOut?: (navigate: NavigateFunction) => boolean;
  lang: string;
  setPath: Dispatch<SetStateAction<string>>;
  path: string;
}

/**
 * Login page component.
 */
const SignInPage: VFC<Props> = (props: Props) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailForReset, setEmailForReset] = useState("");
  const [t, i18n] = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // 挙動情報収集スクリプトをログイン画面の <body> 末尾に挿入する。
  useBodyScript(OMOTI_COLLECTOR_SCRIPT);

  /**
   * dialog open
   */
  const [open, setOpen] = useState(false);
  /**
   * handle open dialog
   */
  const handleClickOpen = () => {
    setOpen(true);
  };
  /**
   * handle close dialog
   */
  const handleClose = () => {
    setOpen(false);
  };

  /**
   * Backdrop open
   */
  const [backdropOpen, setBackdropOpen] = useState(false);
  /**
   * handle open backdrop
   */
  const handleBackdropOpen = () => {
    setBackdropOpen(true);
  };
  /**
   * handle close backdrop
   */
  const handleBackdropClose = () => {
    setBackdropOpen(false);
  };

  useEffect(() => {
    props.signOut(navigate);
  }, []);

  useEffect(() => {
    if (props.lang) {
      i18n.changeLanguage(props.lang);
      setHtmlLang(props.lang);
    } else {
      i18n.changeLanguage("en");
      setHtmlLang("en");
    }
  }, [i18n, props.lang]);

  const onChangeEmail = (value: string) => {
    setEmail(value);
  };

  const onChangeEmailForReset = (value: string) => {
    setEmailForReset(value);
  };

  const onChangePassword = (value: string) => {
    setPassword(value);
  };

  const isValidEmail = (emailAddress: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailAddress);
  };

  const onFailedSignin = () => {
    enqueueSnackbar(t("sign_in.fail"), { variant: "error" });
    handleBackdropClose();
  };

  const onSucceededSignin = () => {
    // 認証成功イベントの直後に、認証成否通知スクリプトを <body> 末尾へ挿入して
    // 1 回だけ発火させる。body に追加するため、この後 SignInPage が
    // アンマウントされてもスクリプトは実行される。
    appendBodyScript(OMOTI_OV_SCRIPT);
    enqueueSnackbar(t("sign_in.success"), { variant: "success" });
    handleBackdropClose();
  };

  const onClick = () => {
    props.setPath(props.path);
    handleBackdropOpen();
    if (props.signIn) {
      props.signIn(
        email.toLocaleLowerCase(),
        password,
        navigate,
        onSucceededSignin,
        onFailedSignin,
        props.path
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing || e.key !== "Enter") return;
    onClick();
  };

  const { post: postReset } = usePost({
    url: `${process.env.REACT_APP_DOMAIN_V3}/api/v3/auth/forgot-password`,
    params: {
      email: emailForReset,
    },
    onSuccess: () => {
      enqueueSnackbar(t("sign_in.send code"), {
        variant: "success",
      });
      handleClose();
      setEmailForReset("");
    },
    onError: () => {
      enqueueSnackbar(t("error.login_again"), {
        variant: "error",
      });
    },
  });

  return (
    <main className="flex-grow flex justify-center items-center">
      <Backdrop
        open={backdropOpen}
        onClick={handleBackdropClose}
        style={{ zIndex: 10001 }}
      >
        <CircularProgress />
      </Backdrop>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <div className="p-8">
          <div className="flex justify-between items-center">
            <span className="text text-lg font-bold">
              {t("sign_in.password_reset.title")}
            </span>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </div>
          <div className="py-4">
            <InputField
              label={t("sign_in.email.label")}
              type="email"
              value={emailForReset}
              onChange={onChangeEmailForReset}
              placeholder={t("sign_in.email.placeholder")}
            />
          </div>
          <div className="flex justify-end">
            <Button
              text={t("general.send")}
              disabled={emailForReset === "" || !isValidEmail(emailForReset)}
              onClick={() => {
                postReset({});
              }}
            />
          </div>
        </div>
      </Dialog>
      <div className="shadow-md max-w-sp w-96 p-8 bg-white rounded-2xl">
        <div className="flex justify-center w-full text-2xl font-bold">
          {t("sign_in.title")}
        </div>
        <div className="grid grid-cols-1 gap-3">
          <div>
            <InputField
              label={t("sign_in.email.label")}
              type="text"
              value={email}
              onChange={onChangeEmail}
              placeholder={t("sign_in.email.placeholder")}
            />
          </div>
          <div>
            <InputField
              label={t("sign_in.password.label")}
              type="password"
              value={password}
              onChange={onChangePassword}
              placeholder={t("sign_in.password.placeholder")}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={handleClickOpen}
            >
              {t("sign_in.forgot_password")}
            </button>
          </div>
          {/* TODO: Add the features to save information and to reveal password. */}
          <div>
            <Button onClick={onClick} text={t("sign_in.button")} isFullWidth />
          </div>
        </div>
      </div>
    </main>
  );
};

export default SignInPage;
